import {
  playerApi,
  type PlayerNotificationDeliveryTelemetry,
} from '../api/public.ts'
import type { PlayerSoundDeliveryEvent } from './readyCheckAlert.ts'

type DeliveryEnvironment = {
  visibilityState: () => 'visible' | 'hidden' | 'prerender'
  hasFocus: () => boolean
  wasDiscarded: () => boolean
  notificationPermission: () => 'default' | 'denied' | 'granted' | 'unsupported'
}

type PlayerNotificationDeliveryReporterOptions = {
  authenticated: () => boolean
  actorKey?: () => string
  send?: (
    telemetry: PlayerNotificationDeliveryTelemetry,
    keepalive: boolean,
  ) => Promise<void>
  environment?: DeliveryEnvironment
  maxQueueSize?: number
}

export function createPlayerNotificationDeliveryReporter(
  options: PlayerNotificationDeliveryReporterOptions,
): (event: PlayerSoundDeliveryEvent) => void {
  const send = options.send ?? playerApi.recordNotificationDelivery
  const environment = options.environment ?? browserDeliveryEnvironment
  const maxQueueSize = Math.max(1, Math.trunc(options.maxQueueSize ?? 64))
  const queue: Array<{
    actorKey: string
    critical: boolean
    telemetry: PlayerNotificationDeliveryTelemetry
  }> = []
  let draining = false

  return event => {
    if (!options.authenticated()) return
    const telemetry = buildPlayerNotificationDeliveryTelemetry(event, environment)
    const item = {
      actorKey: options.actorKey?.() ?? '',
      critical: event.kind === 'ready_check' || event.kind === 'match_connect',
      telemetry,
    }
    if (queue.length >= maxQueueSize) {
      const nonCritical = queue.findIndex(candidate => !candidate.critical)
      if (nonCritical >= 0) {
        queue.splice(nonCritical, 1)
      } else if (item.critical) {
        queue.shift()
      } else {
        return
      }
    }
    const firstNonCritical = item.critical
      ? queue.findIndex(candidate => !candidate.critical)
      : -1
    if (firstNonCritical >= 0) {
      queue.splice(firstNonCritical, 0, item)
    } else {
      queue.push(item)
    }
    void drain()
  }

  async function drain(): Promise<void> {
    if (draining) return
    draining = true
    try {
      while (queue.length > 0) {
        const item = queue.shift()
        if (!item || !options.authenticated()) continue
        if (options.actorKey && options.actorKey() !== item.actorKey) continue
        try {
          // Ordinary delivery uses a bounded single-flight queue. `keepalive`
          // is deliberately false so diagnostics cannot consume the browser's
          // small unload-request budget or compete with critical app traffic.
          await send(item.telemetry, false)
        } catch {
          // Delivery diagnostics are intentionally best-effort. They must
          // never alter notification, matchmaker, or session behavior.
        }
      }
    } finally {
      draining = false
      if (queue.length > 0) void drain()
    }
  }
}

export function buildPlayerNotificationDeliveryTelemetry(
  event: PlayerSoundDeliveryEvent,
  environment: DeliveryEnvironment = browserDeliveryEnvironment,
): PlayerNotificationDeliveryTelemetry {
  const secondsRemaining = Number.isFinite(event.expiresAt)
    ? Math.min(
        86_400,
        Math.max(0, Math.ceil(((event.expiresAt ?? 0) - event.occurredAt) / 1000)),
      )
    : undefined
  const audioErrorName = event.errorName && (
    event.outcome === 'audio_failed' || event.outcome === 'stale'
  )
    ? safeIdentifier(event.errorName, 64)
    : ''
  const serverEventAt = validDateTime(event.serverEventAt)
    ? event.serverEventAt
    : undefined

  return {
    event_type: event.kind,
    event_key: stableTelemetryEventKey(event.kind, event.eventKey),
    outcome: event.outcome,
    observed_at: new Date(event.occurredAt).toISOString(),
    ...(serverEventAt ? { server_event_at: serverEventAt } : {}),
    ...(secondsRemaining !== undefined ? { seconds_remaining: secondsRemaining } : {}),
    visibility_state: environment.visibilityState(),
    has_focus: environment.hasFocus(),
    was_discarded: environment.wasDiscarded(),
    notification_permission: environment.notificationPermission(),
    ...(audioErrorName ? { audio_error_name: audioErrorName } : {}),
    tab_id: stableTelemetryTabID(event.tabID),
  }
}

export function stableTelemetryEventKey(
  kind: PlayerSoundDeliveryEvent['kind'],
  eventKey: string,
): string {
  return `${kind}.${fnv1a(eventKey, 2_166_136_261)}${fnv1a(eventKey, 3_332_467_573)}`
}

export function stableTelemetryTabID(tabID: string): string {
  return `${fnv1a(tabID, 2_166_136_261)}${fnv1a(tabID, 3_332_467_573)}`
}

const browserDeliveryEnvironment: DeliveryEnvironment = {
  visibilityState: () => {
    if (typeof document === 'undefined') return 'hidden'
    const visibility = document.visibilityState as string
    return visibility === 'prerender'
      ? 'prerender'
      : document.hidden ? 'hidden' : 'visible'
  },
  hasFocus: () => {
    if (typeof document === 'undefined' || typeof document.hasFocus !== 'function') {
      return false
    }
    try {
      return document.hasFocus()
    } catch {
      return false
    }
  },
  wasDiscarded: () => {
    if (typeof document === 'undefined') return false
    return (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true
  },
  notificationPermission: () => {
    if (typeof Notification === 'undefined') return 'unsupported'
    return Notification.permission
  },
}

function fnv1a(value: string, seed: number): string {
  let hash = seed >>> 0
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16_777_619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function safeIdentifier(value: string, maxLength: number): string {
  return value
    .replace(/[^A-Za-z0-9_.-]/g, '_')
    .slice(0, maxLength)
}

function validDateTime(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0 && Number.isFinite(Date.parse(value))
}
